<template>
  <td 
    class="editableCell"
    :class="{ 'cellFocused': isFocused && !invalid && !isEditing, 
      'cellFocusedInvalid': isFocused && invalid && !isEditing, 
      'cellInvalid': invalid && !isFocused && !isEditing }"
    @click="setFocus"
  >
    <div class="cellWrapper">
      <!-- Editing mode -->
      <div v-if="isEditing" class="cellContentWrapper">
        <div
          class="cellContent"
          :class="{ 'cellContentInvalid': invalid, 'cellEditing': !invalid }"
          contenteditable="true"
          ref="inputRef"
          @keydown="handleKeydown"
          @blur="finishEdit"
          @wheel.prevent="handleWheel"
          @paste="handlePaste"
        >
          {{ value }}
        </div>

        <!-- Botones de spin -->
        <div class="spinButtons" :class="{ 'spinInvalid': invalid, 'spinEditing': !invalid }">
          <button type="button" tabindex="-1" @mousedown.prevent="spinUp">▲</button>
          <button type="button" tabindex="-1" @mousedown.prevent="spinDown">▼</button>
        </div>
      </div>


      <!-- Display mode -->
      <span 
        v-else 
        class="cellDisplay"
        :class="{ 'nonEditable': !editable }"
        @dblclick="requestEdit"
        @keydown="handleDisplayKeydown"
        tabindex="0"
        ref="displayRef"
      >
        {{ valueFormatted }}
      </span>
    </div>
  </td>
</template>

<script>
import { EventBus } from '@/event-bus.js';
import validationMixin from '@/common/validationMixin.js';
import ShowMessage from '@/components/messages/ShowMessage.js';

export default {
  name: 'EditableNumericCell',
  mixins: [validationMixin, ShowMessage],
  props: {
    name: { type: String, required: true },
    value: [String, Number],
    formatter: { type: Function, default: null },
    editable: { type: Boolean, default: true },
    isFocused: { type: Boolean, default: false },
    validatorRules: { type: Array, default: () => [] },
    inputType: { type: String, default: 'integer' }, //Options 'integer' or 'decimal'
    step: { type: Number, default: 1 },
    min: { type: Number, default: null },
    max: { type: Number, default: null },
    round: { type: Number, default: 2 }
  },
  data() {
    return {
      isEditing: false,
      invalid: false
    };
  },
  computed: {
    valueFormatted() {
      return this.formatter ? this.formatter(this.value) : this.value;
    },
    row() {
      const split = this.name.split('-');
      return split.length === 3 ? parseInt(split[1]) : -1;
    }
  },
  methods: {
    setFocus() {
      this.$emit('cellFocus', this.row, this.name);
    },
    requestEdit() {
      if (!this.editable) return;
      this.$emit('requestEdit', { row: this.row, name: this.name });
    },
    startEdit(selectAll = true) {
      if (!this.editable) {
        this.$nextTick(() => this.$refs.displayRef.focus());
        return;
      }

      this.isEditing = true;
      this.$nextTick(() => {
        const el = this.$refs.inputRef;
        el.focus();

        if (selectAll) {
          const range = document.createRange();
          range.selectNodeContents(el);

          const sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(range);
        } else {
          this.moveCaretToEnd(el);
        }
      });
    },
    finishEdit(event) {
      const val = event.target.innerText.trim();
      const changed = val !== String(this.value).trim();

      // Solo emitir si cambió respecto al prop value
      if (changed) {
        this.$emit('input', val);
      }

      const valid = this.validateCell(val);

      if (valid) {
        this.isEditing = false;
        
        if (changed) {
          this.emitChanged(val);
        }
      } else {
        this.isEditing = true;
        //this.$emit('cellFocus', this.row, this.name);
        //this.$nextTick(() => this.$refs.inputRef.focus());
        this.showMessage(this.$tc('Msg.Error'), this.invalidMessage(this.name), "errorAutoHide");
      }
    },

    // Restricción de teclas + navegación Excel-like
    async handleKeydown(event) {
      const isMac = navigator.platform.toUpperCase().includes('MAC'); 
      const isPasteCombo = (!isMac && event.ctrlKey && event.key.toLowerCase() === 'v') ||
        (isMac && event.metaKey && event.key.toLowerCase() === 'v'); 
        
      if (isPasteCombo) { 
        event.preventDefault(); 
        // Leer texto del portapapeles 
        let paste = '';
        try { 
          paste = await navigator.clipboard.readText();
        } catch (e) { 
          // Fallback: si el API falla, intenta usar el texto que el navegador habría insertado 
          // En muchos casos no está disponible, así que mantenemos cadena vacía. 
          // Como último recurso, deja pasar el evento (quita preventDefault) si prefieres. 
        } 
        this.processPaste(paste); 
        return; 
      }


      // Permitir dígitos
      if (/^[0-9]$/.test(event.key)) return;
      // Permitir coma/punto si es decimal
      if (this.inputType === 'decimal' && (event.key === ',' || event.key === '.')) return;
      // Permitir teclas de control
      const allowedKeys = ['Backspace','Delete','ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Tab','Enter'];
      if (allowedKeys.includes(event.key)) {
        // Lógica de navegación Excel-like
        switch (event.key) {
          case 'Enter':
            event.preventDefault();
            this.finishEdit(event);
            if (!this.invalid) this.$emit('nextCell');
            break;
          case 'Tab':
            event.preventDefault();
            this.finishEdit(event);
            if (!this.invalid) {
              this.$emit(event.shiftKey ? 'prevCell' : 'nextCell');
            }
            break;
          case 'ArrowRight':
            if (this.caretAtEnd()) {
              event.preventDefault();
              this.finishEdit(event);
              if (!this.invalid) this.$emit('nextCell');
            }
            break;
          case 'ArrowLeft':
            if (this.caretAtStart()) {
              event.preventDefault();
              this.finishEdit(event);
              if (!this.invalid) this.$emit('prevCell');
            }
            break;
          case 'ArrowUp':
            // aquí puedes decidir: usar como spin o moverte de celda
            event.preventDefault();
            if (event.altKey) { // ejemplo: Alt+Up = spin
              this.spinUp();
            } else {
              this.finishEdit(event);
              if (!this.invalid) this.$emit('upCell');
            }
            break;
          case 'ArrowDown':
            event.preventDefault();
            if (event.altKey) { // ejemplo: Alt+Down = spin
              this.spinDown();
            } else {
              this.finishEdit(event);
              if (!this.invalid) this.$emit('downCell');
            }
            break;
        }
        return;
      }

      // Bloquear cualquier otra tecla
      event.preventDefault();
    },

    caretAtEnd() {
      const el = this.$refs.inputRef;
      const sel = window.getSelection();
      if (!el || !sel || sel.rangeCount === 0) return false;

      const range = sel.getRangeAt(0);
      const end = document.createRange();
      end.selectNodeContents(el);
      end.collapse(false); // colapsa al final

      // Si el final de la selección coincide con el final del contenido
      return range.compareBoundaryPoints(Range.END_TO_END, end) === 0;
    },

    caretAtStart() {
      const el = this.$refs.inputRef;
      const sel = window.getSelection();
      if (!el || !sel || sel.rangeCount === 0) return false;

      const range = sel.getRangeAt(0);
      const start = document.createRange();
      start.selectNodeContents(el);
      start.collapse(true); // colapsa al inicio

      return range.compareBoundaryPoints(Range.START_TO_START, start) === 0;
    },

    spinUp() {
      let current = parseFloat(this.value) || 0;
      current += this.step;
      if (this.max !== null && current > this.max) current = this.max;

      // aplicar redondeo
      if (this.inputType === 'decimal') {
        const decimals = this.round != null ? this.round : 2;
        current = parseFloat(current.toFixed(decimals));
      } else {
        current = Math.round(current);
      }

      const el = this.$refs.inputRef;
      el.innerText = this.value; // usa el computed

      this.$emit('input', String(current));
      this.emitChanged(current);
      this.validateCell(String(current));

      this.$nextTick(() => {
        el.focus();
        this.moveCaretToEnd(el);
      });
    },

    spinDown() {
      let current = parseFloat(this.value) || 0;
      current -= this.step;
      if (this.min !== null && current < this.min) current = this.min;

      if (this.inputType === 'decimal') {
        const decimals = this.round != null ? this.round : 2;
        current = parseFloat(current.toFixed(decimals));
      } else {
        current = Math.round(current);
      }

      const el = this.$refs.inputRef;
      el.innerText = this.value;

      this.$emit('input', String(current));
      this.emitChanged(current);
      this.validateCell(String(current));

      this.$nextTick(() => {
        el.focus();
        this.moveCaretToEnd(el);
      });
    },
    handlePaste(event) {
      console.log('handlePaste', event)
      event.preventDefault();
      const paste = (event.clipboardData || window.clipboardData).getData('text');
      this.processPaste(paste);
    },
    processPaste(paste) {
      let clean = paste;
      if (this.inputType === 'integer') {
        clean = clean.replace(/[^0-9]/g, '');
        if (clean !== '') clean = String(Math.round(parseFloat(clean)));
      } else if (this.inputType === 'decimal') {
        clean = clean.replace(/[^0-9.,]/g, '').replace(',', '.');
        if (clean !== '') {
          let num = parseFloat(clean);
          if (!isNaN(num)) {
            const decimals = this.round != null ? this.round : 2;
            num = parseFloat(num.toFixed(decimals));
            clean = num.toFixed(decimals);
          }
        }
      }
      const el = this.$refs.inputRef;
      el.innerText = clean;
      this.$emit('input', clean);
      this.validateCell(clean);
      this.$nextTick(() => {
        el.focus();
        this.moveCaretToEnd(el);
      });
    },

    moveCaretToEnd(el) {
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false); // al final
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    },

    handleWheel(event) {
      if (this.editable && this.isFocused) {
        event.deltaY < 0 ? this.spinUp(): this.spinDown();
      }
    },

    handleDisplayKeydown(event) {
      switch (event.key) {
        case 'Enter':
          event.preventDefault();

          if (this.editable) {
            this.requestEdit();
          } else {
            this.$emit('nextCell'); 
          }
          break;

        case 'F2': // opcional, estilo Excel
          if (this.editable) {
            event.preventDefault();
            this.requestEdit();
          }
          break;

        case 'Tab':
          event.preventDefault();
          this.$emit(event.shiftKey ? 'prevCell' : 'nextCell');
          break;

        case 'ArrowUp':
          event.preventDefault();
          this.$emit('upCell');
          break;

        case 'ArrowDown':
          event.preventDefault();
          this.$emit('downCell');
          break;

        case 'ArrowLeft':
          event.preventDefault();
          this.$emit('prevCell');
          break;

        case 'ArrowRight':
          event.preventDefault();
          this.$emit('nextCell');
          break;
      }
    },

    emitChanged(value) {
      EventBus.$emit('onChanged', { Changed: true, componentName: this.name, value });
    },
    validateCell(val = null) {
      let state = true;
      const valueToCheck = val !== null ? val : String(this.value ?? '').trim();

      if (this.validatorRules) {
        const rules = {
          [this.name]: this.validatorRules
        };
        state = this.validateFields(rules, { [this.name]: valueToCheck });
      }

      this.invalid = !state;
      if (this.isFocused) {
        this.$emit('validationState', { name: this.name, invalid: this.invalid });
      }

      return state;
    },
  }
};
</script>

<style scoped>
.editableCell { padding: 0 !important; min-width: 120px; }
.cellFocused { box-shadow: inset 0 0 0 1px #007bff !important; }
.cellWrapper { position: relative; display: flex; align-items: center; width: 100%; height: 100%; align-items: stretch; }
.cellDisplay { flex: 1; padding: 8px; cursor: pointer; text-align: right; }
.nonEditable { cursor: default; color: #555; }
.cellEditing { flex: 1; padding: 6px; outline: none; width: calc(100% - 6px);
  border: 2px solid #007bff; border-radius: 4px; background-color: #fff; 
  cursor: text;}
.cellContentInvalid { flex: 1; padding: 6px; outline: none; width: calc(100% - 6px);
  border: 2px solid red; border-radius: 4px; background-color: #fff; }
.cellInvalid { background-color: #fae3de !important; box-shadow: inset 0 0 0 1px red !important; }
.cellFocusedInvalid { background-color: #fae3de !important; box-shadow: inset 0 0 0 1px #007bff !important; }
.cellContentWrapper {
  display: flex;
  align-items: stretch; /* estira editable y spin */
  width: 100%;
  height: 100%;
}
.cellContent {
  flex: 1;
  padding: 6px;
  outline: none;
  text-align: right;
  /*border: 2px solid #007bff;*/
  border-right: none;
  border-radius: 4px 0 0 4px;
  background-color: #fff;
  height: 100%; /* ocupa toda la celda */
  box-sizing: border-box;
}
.spinButtons {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  /*border: 2px solid #007bff;*/
  border-left: none;
  border-radius: 0 4px 4px 0;
  box-sizing: border-box;
  margin-right: 2px;
}
.spinButtons button {
  flex: 1;              /* cada botón ocupa la mitad */
  padding: 0;           /* elimina padding extra */
  margin: 0;
  cursor: pointer;
  background: #f0f0f0;
  border: none;
  font-size: 12px;      /* ajusta tamaño del símbolo ▲▼ */
}
.spinInvalid {
  border: 2px solid red;
  border-left: none;
}
.spinEditing {
  border: 2px solid #007bff;
  border-left: none;
}
</style>
